import axiosInstance from '../helpers/axios';
import { ICompanyInfo } from '../models/ICompany';
import { IUser } from '../models/IUser';
import {
  TAvailableModule,
  TCalendarEvent,
  TCart,
  TCheckoutPayment,
  TClass,
  TCouponValidation,
  TCourse,
  TCourseFilters,
  TCourseGroup,
  TInitialCheckoutResult,
  TOpsAccount360,
  TOpsAccountHit,
  TOpsAccountList,
  TOpsAccountUpdate,
  TOpsAssignmentCreate,
  TOpsAssignmentList,
  TOpsInstructorRates,
  TOpsProductList,
  TOpsProgramDetail,
  TOpsProgramList,
  TOpsProductUpsert,
  TOpsRegistrationCreate,
  TOpsRegistrationList,
  TOpsCalendarEvent,
  TOpsCaseDetail,
  TOpsCaseList,
  TOpsEvalOverview,
  TOpsClassDetail,
  TOpsClassList,
  TOpsClassExceptions,
  TOpsClassRelated,
  TOpsClassUpsert,
  TOpsContactDetail,
  TOpsAccountEvals,
  TOpsContactHit,
  TOpsContactList,
  TOpsContactUpsert,
  TOpsClassesMeta,
  TOpsSchedulingMeta,
  TOpsTemplateList,
  TOpsTemplateRender,
  TInviteDetails,
  TInviteRequest,
  TInviteResponse,
  TLogin,
  TPrivateCoursesResponse,
  TProgramInvite,
  TProgramInviteValues,
  TUploadResponse,
} from '../types';

export const getInviteDetails = async (token?: string) => {
  const params = token ? { token } : null;
  const response = await axiosInstance.get<TInviteDetails>('invite-details', {
    params,
  });
  return response.data;
};

export const acceptInvite = async (data: TInviteRequest) => {
  const response = await axiosInstance.post<TInviteResponse>(
    'invites/accept',
    data,
  );
  return response.data;
};

export const signUp = async (userInfo: Partial<IUser>) => {
  const response = await axiosInstance.post<{ email?: string }>(
    'sign-up',
    userInfo,
  );
  return response.data;
};

export const setPassword = async (password: string, token?: string) => {
  const response = await axiosInstance.post<TLogin>('set-password', {
    token,
    password,
  });
  return response.data;
};

export const login = async (email: string, password?: string) => {
  const response = await axiosInstance.post<Partial<TLogin>>('login', {
    email,
    password,
  });
  return response.data;
};

export const samlCheck = async (email: string) => {
  const response = await axiosInstance.post<{
    companyId: string;
    samlMode: string;
  }>('saml/check', { email });
  return response.data;
};

export const logout = async () => {
  return axiosInstance.post('sign-out');
};

export const getMe = async () => {
  const response = await axiosInstance.get<IUser>('me');
  return response.data;
};

export const upcomingClasses = async () => {
  const response = await axiosInstance.get<TClass[]>('classes/upcoming');
  return response.data;
};

export const pastClasses = async (
  fromDate: string | undefined,
  signal: AbortSignal,
) => {
  const response = await axiosInstance.get<TClass[]>('classes/past', {
    params: { fromDate },
    signal,
  });
  return response.data;
};

export const getCompany = async () => {
  const response = await axiosInstance.get<ICompanyInfo>('companies');
  return response.data;
};

export const getCalendarEvents = async (params?: { bookedOnly: boolean }) => {
  const response = await axiosInstance.get<TCalendarEvent[]>(
    'courses/calendar-events',
    { params },
  );
  return response.data;
};

export const getCourseFilters = async () => {
  const response = await axiosInstance.get<TCourseFilters>('courses/filters');
  return response.data;
};

export const getPublicCourses = async () => {
  const response = await axiosInstance.get<TCourseGroup[]>('courses/public');
  return response.data;
};

export const getPrivateCourses = async () => {
  const response =
    await axiosInstance.get<TPrivateCoursesResponse>('courses/private');
  return response.data;
};

export const getPublicCourse = async (slug?: string) => {
  const response = await axiosInstance.get<TCourse>(`courses/${slug}`);
  return response.data;
};

export const getCourseAvailableTimes = async (parentCourseId: string) => {
  const response = await axiosInstance.get<TAvailableModule[]>(
    `courses/${parentCourseId}/available-times`,
  );
  return response.data;
};

export const validateCoupon = async (couponCode: string, amount: number) => {
  const response = await axiosInstance.post<TCouponValidation>(
    'checkout/validate-coupon',
    { couponCode, amount },
  );
  return response.data;
};

export const checkout = async (cart: TCart) => {
  const response = await axiosInstance.post<TInitialCheckoutResult>(
    'checkout',
    cart,
  );
  return response.data;
};

export const createPaymentIntent = async (checkout: TCheckoutPayment) => {
  const response = await axiosInstance.post<{ clientSecret: string }>(
    `checkout/create-payment-intent`,
    checkout,
  );
  return response.data;
};

export const markAttendanceForClass = async (registrationId: string) => {
  await axiosInstance.post(`classes/${registrationId}/attend`);
};

export const requestClassCancel = async (registrationId: string) => {
  await axiosInstance.post(`classes/${registrationId}/request-cancel`);
};

export const getProgramInvites = async () => {
  const response =
    await axiosInstance.get<TProgramInvite[]>('invites/programs');
  return response.data;
};

export const deleteInvite = async (id: string) => {
  return await axiosInstance.delete(`invites/${id}`);
};

export const generateProgramInvite = async (data: TProgramInviteValues) => {
  const response = await axiosInstance.post<TProgramInvite>('invites', data);
  return response.data;
};

async function uploadImage(url: string, image: Blob | null) {
  const formData = new FormData();
  if (image) {
    formData.append('image', image);
  }
  const response = await axiosInstance.post<TUploadResponse>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export const uploadCompanyLogo = async (image: Blob | null) => {
  return await uploadImage('companies/logo', image);
};

export const uploadUserAvatar = async (image: Blob | null) => {
  return await uploadImage('users/avatar', image);
};

export const updateSamlConfig = async (config: {
  enableSaml: boolean;
  enforceSaml: boolean;
  idpMetadataUrl?: string;
  idpMetadataXml?: string;
}) => {
  const response = await axiosInstance.patch('companies/saml', config);
  return response.data;
};

export const joinWaitlist = async (sfLearnitCourseId: string) => {
  const response = await axiosInstance.post(`classes/waitlist`, null, {
    params: { sfLearnitCourseId },
  });
  return response.data;
};

export const isImpersonationAllowed = async () => {
  const response = await axiosInstance.get<{ allowed: boolean }>(
    '/impersonation/allowed',
  );
  return response.data;
};

export const impersonate = async (userEmail: string) => {
  const response = await axiosInstance.post<{ accessToken: string }>(
    `/impersonate`,
    { email: userEmail },
  );
  return response.data;
};

export const downloadCertificate = async (registrationId: string) => {
  const response = await axiosInstance.get(
    `classes/${registrationId}/download-certificate`,
    {
      responseType: 'blob',
    },
  );
  return response.data;
};

// ── Ops (staff) · scheduling migration ─────────────────────────────────────

export const getOpsSchedulingMeta = async () => {
  const response = await axiosInstance.get<TOpsSchedulingMeta>(
    'ops/scheduling/meta',
  );
  return response.data;
};

export const getOpsCalendar = async (from: string, to: string) => {
  const response = await axiosInstance.get<TOpsCalendarEvent[]>(
    'ops/scheduling/calendar',
    { params: { from, to } },
  );
  return response.data;
};

export const getOpsClasses = async (params: {
  q?: string;
  status?: string;
  classType?: string;
  unassigned?: boolean;
  page?: number;
  when?: 'upcoming' | 'past' | 'all';
  prepCall?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  accountId?: string;
  programId?: string;
  instructor?: string;
}) => {
  const response = await axiosInstance.get<TOpsClassList>('ops/classes', {
    params: { ...params, unassigned: params.unassigned ? 1 : undefined },
  });
  return response.data;
};

export const getOpsClassExceptions = async (days = 90) => {
  const response = await axiosInstance.get<TOpsClassExceptions>(
    'ops/classes/exceptions',
    { params: { days } },
  );
  return response.data;
};

export const getOpsClass = async (id: string) => {
  const response = await axiosInstance.get<TOpsClassDetail>(
    `ops/classes/${id}`,
  );
  return response.data;
};

export const createOpsClass = async (dto: TOpsClassUpsert) => {
  const response = await axiosInstance.post<TOpsClassDetail>(
    'ops/classes',
    dto,
  );
  return response.data;
};

export const updateOpsClass = async (id: string, dto: TOpsClassUpsert) => {
  const response = await axiosInstance.patch<TOpsClassDetail>(
    `ops/classes/${id}`,
    dto,
  );
  return response.data;
};

export const getOpsClassesMeta = async () => {
  const response = await axiosInstance.get<TOpsClassesMeta>('ops/classes/meta');
  return response.data;
};

export const searchOpsAccounts = async (q: string) => {
  const response = await axiosInstance.get<TOpsAccountHit[]>(
    'ops/classes/accounts',
    { params: { q } },
  );
  return response.data;
};

export const getOpsAssignments = async (params: {
  instructor?: string;
  q?: string;
  upcoming?: boolean;
  page?: number;
}) => {
  const response = await axiosInstance.get<TOpsAssignmentList>(
    'ops/assignments',
    { params: { ...params, upcoming: params.upcoming ? 'true' : undefined } },
  );
  return response.data;
};

export const getOpsInstructors = async () => {
  const response = await axiosInstance.get<TOpsInstructorRates[]>(
    'ops/assignments/instructors',
  );
  return response.data;
};

export const createOpsAssignment = async (dto: TOpsAssignmentCreate) => {
  const response = await axiosInstance.post('ops/assignments', dto);
  return response.data;
};

export const removeOpsAssignment = async (id: string) => {
  const response = await axiosInstance.delete(`ops/assignments/${id}`);
  return response.data;
};

export const getOpsRegistrations = async (params: {
  q?: string;
  status?: string;
  category?: string;
  classId?: string;
  page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  contactId?: string;
  from?: string;
  to?: string;
}) => {
  const response = await axiosInstance.get<TOpsRegistrationList>(
    'ops/registrations',
    { params },
  );
  return response.data;
};

export const searchOpsContacts = async (q: string) => {
  const response = await axiosInstance.get<TOpsContactHit[]>(
    'ops/registrations/contacts',
    { params: { q } },
  );
  return response.data;
};

export const createOpsRegistration = async (dto: TOpsRegistrationCreate) => {
  const response = await axiosInstance.post('ops/registrations', dto);
  return response.data;
};

export const setOpsRegistrationStatus = async (id: string, status: string) => {
  const response = await axiosInstance.patch(`ops/registrations/${id}/status`, {
    status,
  });
  return response.data;
};

export const getOpsContacts = async (params: {
  q?: string;
  scope?: 'name' | 'email' | 'account';
  accountId?: string;
  page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}) => {
  const response = await axiosInstance.get<TOpsContactList>('ops/contacts', {
    params,
  });
  return response.data;
};

export const getOpsContact = async (id: string) => {
  const response = await axiosInstance.get<TOpsContactDetail>(
    `ops/contacts/${id}`,
  );
  return response.data;
};

export const createOpsContact = async (dto: TOpsContactUpsert) => {
  const response = await axiosInstance.post<TOpsContactDetail>(
    'ops/contacts',
    dto,
  );
  return response.data;
};

export const updateOpsContact = async (id: string, dto: TOpsContactUpsert) => {
  const response = await axiosInstance.patch<TOpsContactDetail>(
    `ops/contacts/${id}`,
    dto,
  );
  return response.data;
};

export const getOpsCatalogue = async (params: {
  q?: string;
  family?: string;
  active?: boolean;
  page?: number;
}) => {
  const response = await axiosInstance.get<TOpsProductList>('ops/catalogue', {
    params: { ...params, active: params.active ? 'true' : undefined },
  });
  return response.data;
};

export const updateOpsProduct = async (id: string, dto: TOpsProductUpsert) => {
  const response = await axiosInstance.patch(`ops/catalogue/${id}`, dto);
  return response.data;
};

export const getOpsPrograms = async (params: {
  q?: string;
  status?: string;
  page?: number;
}) => {
  const response = await axiosInstance.get<TOpsProgramList>('ops/programs', {
    params,
  });
  return response.data;
};

export const getOpsProgram = async (id: string) => {
  const response = await axiosInstance.get<TOpsProgramDetail>(
    `ops/programs/${id}`,
  );
  return response.data;
};

export const addOpsProgramMember = async (
  programId: string,
  contactId: string,
) => {
  const response = await axiosInstance.post(
    `ops/programs/${programId}/members`,
    { contactId },
  );
  return response.data;
};

export const setOpsProgramMember = async (id: string, enabled: boolean) => {
  const response = await axiosInstance.patch(`ops/programs/members/${id}`, {
    enabled,
  });
  return response.data;
};

export const getOpsCases = async (params: {
  q?: string;
  status?: string;
  all?: boolean;
  page?: number;
}) => {
  const response = await axiosInstance.get<TOpsCaseList>('ops/cases', {
    params: { ...params, all: params.all ? 'true' : undefined },
  });
  return response.data;
};

export const getOpsCase = async (id: string) => {
  const response = await axiosInstance.get<TOpsCaseDetail>(`ops/cases/${id}`);
  return response.data;
};

export const setOpsCaseStatus = async (id: string, status: string) => {
  const response = await axiosInstance.patch(`ops/cases/${id}/status`, {
    status,
  });
  return response.data;
};

export const getOpsAccounts = async (params: {
  q?: string;
  page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}) => {
  const response = await axiosInstance.get<TOpsAccountList>('ops/accounts', {
    params,
  });
  return response.data;
};

export const getOpsAccount = async (id: string) => {
  const response = await axiosInstance.get<TOpsAccount360>(
    `ops/accounts/${id}`,
  );
  return response.data;
};

export const updateOpsAccount = async (id: string, dto: TOpsAccountUpdate) => {
  const response = await axiosInstance.patch<TOpsAccount360>(
    `ops/accounts/${id}`,
    dto,
  );
  return response.data;
};

export const getOpsTemplates = async (params: {
  q?: string;
  page?: number;
}) => {
  const response = await axiosInstance.get<TOpsTemplateList>(
    'ops/comms/templates',
    { params },
  );
  return response.data;
};

export const renderOpsTemplate = async (id: string, recordId?: string) => {
  const response = await axiosInstance.get<TOpsTemplateRender>(
    `ops/comms/templates/${id}/render`,
    { params: recordId ? { recordId } : {} },
  );
  return response.data;
};

export const getOpsEvals = async (params: { q?: string; page?: number }) => {
  const response = await axiosInstance.get<TOpsEvalOverview>('ops/evals', {
    params,
  });
  return response.data;
};

export const getOpsAccountEvals = async (accountId: string) => {
  const response = await axiosInstance.get<TOpsAccountEvals>(
    `ops/evals/account/${accountId}`,
  );
  return response.data;
};

export const getOpsClassRelated = async (id: string) => {
  const response = await axiosInstance.get<TOpsClassRelated>(
    `ops/classes/${id}/related`,
  );
  return response.data;
};

export const updateOpsProgram = async (
  id: string,
  dto: { name: string; status: string; startDate: string; endDate: string },
) => {
  const response = await axiosInstance.patch(`ops/programs/${id}`, dto);
  return response.data;
};

export type TOpsContactHistory = {
  contactId: string;
  name: string | null;
  email: string | null;
  registrations: {
    id: string;
    regNumber: string | null;
    courseName: string | null;
    courseStart: string | null;
    status: string | null;
    category: string | null;
    accountName: string | null;
    bookingDate: string | null;
    classId: string | null;
    localOrigin: boolean;
  }[];
  evaluations: {
    id: string;
    courseName: string | null;
    courseDate: string | null;
    evalDate: string | null;
    rating: number | null;
    recommend: string | null;
    feedback: string | null;
    registrationId: string | null;
    classId: string | null;
    linkedBy: 'registration' | 'email';
  }[];
};

/** Every registration and evaluation on record for a person. */
export const getOpsContactHistory = async (id: string) =>
  (await axiosInstance.get<TOpsContactHistory>(`ops/contacts/${id}/history`))
    .data;
